/**
 * usePhotoInstallPrompt — real hook (FT-348 2026-07-08).
 *
 * Replaces the FT-343 stub. Detects iOS Safari (not launched from home screen),
 * exposes an "install nudge" banner state, and persists user response so we
 * don't spam. Per DESIGN-BRIEF.md §5.
 *
 * Storage: localStorage key `sr.photos.install-nudge.v1` shape
 *   { dismissedAt: number, dismissCount: number, addedToHome: boolean }
 *
 * Rules (§5.3):
 *   - addedToHome === true     → never show again
 *   - dismissedAt within 7 days → don't re-show
 *   - dismissCount >= 3         → never show again
 *
 * TODO(FT-fast-follow): Android `beforeinstallprompt` — capture the event on
 *   window and expose a `promptInstall()` action. Nice-to-have; iOS is the
 *   problem child.
 *
 * @returns {{
 *   shouldShow: boolean,
 *   isModalOpen: boolean,
 *   showModal: () => void,
 *   closeModal: () => void,
 *   markInstalled: () => void,
 *   markDismissed: () => void,
 * }}
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'sr.photos.install-nudge.v1';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_DISMISS = 3;

function readState() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return { dismissedAt: 0, dismissCount: 0, addedToHome: false };
    const parsed = JSON.parse(raw);
    return {
      dismissedAt: Number(parsed?.dismissedAt) || 0,
      dismissCount: Number(parsed?.dismissCount) || 0,
      addedToHome: Boolean(parsed?.addedToHome),
    };
  } catch {
    return { dismissedAt: 0, dismissCount: 0, addedToHome: false };
  }
}

function writeState(state) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  } catch {
    /* quota errors, private mode etc — silent noop */
  }
}

/**
 * Detects the iOS-Safari-not-standalone case where the install nudge applies.
 * SSR-safe: returns false when `navigator` / `window` are missing.
 */
export function detectIOSSafariNotStandalone() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const platform = navigator.platform || '';
  const ua = navigator.userAgent || '';
  const isIOS = /iP(hone|ad|od)/.test(platform) || /iP(hone|ad|od)/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  const isIOSSafari = isIOS && isSafari;
  if (!isIOSSafari) return false;
  const mm =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(display-mode: standalone)').matches
      : false;
  const standalone = mm || Boolean(window.navigator?.standalone);
  return !standalone;
}

export function usePhotoInstallPrompt() {
  const [persisted, setPersisted] = useState(readState);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Environment detection is stable across a session — memoise once.
  const envAllows = useMemo(() => detectIOSSafariNotStandalone(), []);

  const shouldShow = useMemo(() => {
    if (!envAllows) return false;
    if (persisted.addedToHome) return false;
    if (persisted.dismissCount >= MAX_DISMISS) return false;
    if (persisted.dismissedAt && Date.now() - persisted.dismissedAt < SEVEN_DAYS_MS) return false;
    return true;
  }, [envAllows, persisted]);

  // Reconcile with storage on mount so cross-tab writes are respected.
  useEffect(() => {
    setPersisted(readState());
  }, []);

  const showModal = useCallback(() => {
    if (!envAllows) return;
    setIsModalOpen(true);
  }, [envAllows]);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const markInstalled = useCallback(() => {
    const next = { ...readState(), addedToHome: true };
    writeState(next);
    setPersisted(next);
    setIsModalOpen(false);
  }, []);

  const markDismissed = useCallback(() => {
    const prev = readState();
    const next = {
      ...prev,
      dismissedAt: Date.now(),
      dismissCount: (prev.dismissCount || 0) + 1,
    };
    writeState(next);
    setPersisted(next);
    setIsModalOpen(false);
  }, []);

  return { shouldShow, isModalOpen, showModal, closeModal, markInstalled, markDismissed };
}

export default usePhotoInstallPrompt;
